import { ClientError, GraphQLClient, type Variables } from 'graphql-request';
import { AsyncResult } from '../core';
import { isAbortError } from '../core/errorUtils';
import { apiErrorResult, IApiOriginalError } from './apiError';
import { ApiRequestParams } from './apiSchema';

const EXPECTED_GRAPHQL_ERRORS = new Set([
  'Not Found',
  'Unauthorized',
  'A revision is already in progress for this report.',
  'The notes or report were edited, changed by feedback, or certified, so they were not re-run.',
  'Invitation not found',
  'Verify your email address to accept this invitation',
  'Username already exists.',
]);

export function isExpectedGraphQlError(message: string) {
  return EXPECTED_GRAPHQL_ERRORS.has(message)
    || message.includes('is already a member of team')
    || message.startsWith('Untick Certify to ');
}

export async function runGraphQlApiRequest<TData = unknown, TVars extends Variables = Variables>(params: ApiRequestParams<TVars>): AsyncResult<TData> {

  const {
    document: doc,
    variables: vars,
    endpoint,
    token
  } = params;

  const client = new GraphQLClient(endpoint, {
    mode: 'cors',
    signal: params.signal,
    headers: params.headers,
  });

  if (token)
    client.setHeader('Authorization', token);

  try {
    const result = await client.request<TData>({ document: doc, variables: vars });
    return [result];
  } catch (err) {

    if (isAbortError(err))
      return apiErrorResult('RequestAborted', null, err);

    if (err instanceof ClientError) {

      const message = err.message.split(':')[0];
      const mfaRequired = (err.response.errors as IApiOriginalError[] | undefined)?.some(error => error.errorType === 'MfaRequired');
      // console.error becomes a Sentry event; outcomes the UI already handles are not faults.
      if (!mfaRequired && err.response.status !== 401 && !isExpectedGraphQlError(message))
        console.error('GraphQL Error', message);

      if (mfaRequired)
        return apiErrorResult('MfaRequired', 'Complete two-factor authentication to continue.', err);

      if (err.response.status === 401)
        return apiErrorResult('ProviderNotAuthorized', message, err);

      else if (message === 'Unauthorized')
        return apiErrorResult('NotAuthorized', message, err);

      return apiErrorResult('ProviderError', message, err);
    }

    const message = err instanceof Error ? err.message : null;
    return apiErrorResult('ProviderError', message, err instanceof Error ? err : undefined);
  }
}
